/**
 * Goods-purchase invoices for second-company staff: periods, invoices per
 * employee, and the printable invoice (three layout families standing in for
 * the legacy's ten PDF templates).
 */
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Reveal } from '../components/motion.tsx';
import { Button, Empty, PageHead, Panel, Skeleton, useToast } from '../components/ui.tsx';
import { ApiError, get, patch, post } from '../lib/api.ts';
import { date, dateRange, idr } from '../lib/format.ts';

interface PeriodRow { id: string; office: string; fromDate: string; toDate: string; totalSalary: string; totalThr: string; totalBonus: string; invoices: number }
interface PeriodDetail extends Omit<PeriodRow, 'invoices'> {
  templates: { id: string; name: string; templateKey: string }[];
  invoices: { id: string; number: string; vendorName: string; templateId: string | null; salaryAmount: string; thrAmount: string; bonusAmount: string; remaining: string; items: number }[];
}
interface Invoice {
  id: string;
  number: string;
  templateId: string | null;
  template: string | null;
  date: string;
  vendor: { name: string; email: string | null; phone: string | null };
  buyer: { name: string; npwp: string | null; email: string | null; phone: string | null };
  period: { from: string; to: string };
  items: { kind: string | null; name: string; unit: string | null; quantity: number; price: string; total: string }[];
}

const SOURCE: Record<string, string> = { salary: 'Salary', thr: 'Holiday allowance', bonus: '15th-month bonus' };

export function GoodsPeriods() {
  const q = useQuery({ queryKey: ['goods-periods'], queryFn: () => get<PeriodRow[]>('/api/goods/periods') });
  return (
    <div>
      <PageHead
        eyebrow="Second company"
        title={
          <>
            Goods-purchase <em>invoices.</em>
          </>
        }
        lede="Second-company staff are paid partly through invoices for goods they supply. Hearth asks the inventory app for items that use up each amount, then prints one invoice per person per period."
      />
      <Reveal>
        <Reveal.Item>
          <Panel eyebrow="Periods" title="By pay period">
            {q.isLoading ? (
              <Skeleton h={160} />
            ) : !q.data?.length ? (
              <Empty title="No invoices yet.">Produce them from an approved payroll or THR run that includes second-company staff.</Empty>
            ) : (
              <div>
                {q.data.map((p) => (
                  <Link key={p.id} to="/payroll/goods/$periodId" params={{ periodId: p.id }} className="run-card">
                    <div>
                      <div className="run-card__period">{dateRange(p.fromDate, p.toDate)}</div>
                      <div className="eyebrow" style={{ marginTop: 6 }}>{p.office}</div>
                    </div>
                    <div className="row row--wrap" style={{ ['--gap' as string]: '20px', fontSize: 'var(--step--1)' }}>
                      <span><span className="faint">Invoices</span> <span className="num">{p.invoices}</span></span>
                      <span><span className="faint">Salary</span> <span className="num">{idr(p.totalSalary)}</span></span>
                      {Number(p.totalThr) ? <span><span className="faint">THR</span> <span className="num">{idr(p.totalThr)}</span></span> : null}
                    </div>
                    <span />
                    <Icon name="chevronRight" width={18} height={18} className="faint" />
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </Reveal.Item>
      </Reveal>
    </div>
  );
}

export function GoodsPeriod() {
  const { periodId } = useParams({ strict: false }) as { periodId: string };
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['goods-period', periodId], queryFn: () => get<PeriodDetail>(`/api/goods/periods/${periodId}`) });
  const setTemplate = useMutation({ mutationFn: (v: { id: string; templateId: string | null }) => patch(`/api/goods/invoices/${v.id}`, { templateId: v.templateId }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['goods-period', periodId] }) });
  const exportM = useMutation({ mutationFn: () => post<{ fileId: string }>(`/api/goods/periods/${periodId}/export`), onSuccess: (r) => window.location.assign(`/api/payroll/files/${r.fileId}`), onError: (e) => toast({ title: e instanceof ApiError ? e.message : 'Export failed', tone: 'error' }) });
  const p = q.data;
  if (!p) return <Skeleton h={360} />;
  return (
    <div>
      <PageHead eyebrow={`Goods purchases · ${p.office}`} title={<>{dateRange(p.fromDate, p.toDate)}</>} lede="Pick a layout for each person (vary them, as before), then print or save each invoice as PDF." aside={<Button icon="download" loading={exportM.isPending} onClick={() => exportM.mutate()}>Items (Excel)</Button>} />
      <Panel flush>
        <table className="table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Vendor (employee)</th>
              <th className="right">Amount</th>
              <th className="right">Unused</th>
              <th>Layout</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {p.invoices.map((i) => (
              <tr key={i.id}>
                <td className="mono">{i.number}</td>
                <td>{i.vendorName}<div className="faint" style={{ fontSize: 'var(--step--2)' }}>{i.items} items</div></td>
                <td className="right num">{idr((Number(i.salaryAmount) + Number(i.thrAmount) + Number(i.bonusAmount)).toFixed(2))}</td>
                <td className="right num faint">{Number(i.remaining) ? idr(i.remaining) : '—'}</td>
                <td>
                  <select className="select select--sm" value={i.templateId ?? ''} onChange={(e) => setTemplate.mutate({ id: i.id, templateId: e.target.value || null })} aria-label={`Layout for ${i.number}`}>
                    <option value="">Default</option>
                    {p.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </td>
                <td className="right"><Link to="/invoices/$id" params={{ id: i.id }}><Button size="sm" variant="ghost">Open</Button></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function InvoicePage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({ queryKey: ['invoice', id], queryFn: () => get<Invoice>(`/api/goods/invoices/${id}`) });
  const [kind, setKind] = useState<string>('all');
  const inv = q.data;
  if (!inv) return <Skeleton h={480} />;
  const items = inv.items.filter((i) => kind === 'all' || i.kind === kind);
  const total = items.reduce((n, i) => n + Number(i.total), 0);
  const kinds = [...new Set(inv.items.map((i) => i.kind ?? 'salary'))];
  const variant = variantOf(inv);
  return (
    <div>
      <div className="row row--between no-print" style={{ margin: 'var(--s-6) 0' }}>
        <span className="faint">Goods-purchase invoice</span>
        <div className="row" style={{ gap: 8 }}>
          {kinds.length > 1 ? (
            <select className="select select--sm" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Source">
              <option value="all">All sources</option>
              {kinds.map((k) => <option key={k} value={k}>{SOURCE[k] ?? k}</option>)}
            </select>
          ) : null}
          <Button size="sm" icon="download" onClick={() => window.print()}>Print / PDF</Button>
        </div>
      </div>
      <article className={`invoice invoice--${variant}`}>
        <header className="invoice__head">
          <div>
            <div className="invoice__vendor">{inv.vendor.name}</div>
            <div className="invoice__meta">{[inv.vendor.phone, inv.vendor.email].filter(Boolean).join(' · ')}</div>
          </div>
          <div className="invoice__title">
            <div>INVOICE</div>
            <div className="invoice__no">{inv.number}</div>
          </div>
        </header>
        <section className="invoice__parties">
          <div>
            <div className="invoice__label">Bill to</div>
            <div>{inv.buyer.name}</div>
            {inv.buyer.npwp ? <div className="invoice__meta">NPWP {inv.buyer.npwp}</div> : null}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="invoice__label">Date</div>
            <div>{date(inv.date)}</div>
          </div>
        </section>
        <table className="invoice__items">
          <thead>
            <tr>
              <th>Item</th>
              <th className="r">Qty</th>
              <th className="r">Price</th>
              <th className="r">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i, n) => (
              <tr key={n}>
                <td>{i.name}{i.unit ? <span className="invoice__meta"> · {i.unit}</span> : null}</td>
                <td className="r">{i.quantity}</td>
                <td className="r">{idr(i.price)}</td>
                <td className="r">{idr(i.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="r">Total</td>
              <td className="r">{idr(total.toFixed(2))}</td>
            </tr>
          </tfoot>
        </table>
        <footer className="invoice__foot">Thank you. Payment by transfer within the pay period {dateRange(inv.period.from, inv.period.to)}.</footer>
      </article>
    </div>
  );
}

/** The chosen layout family, else one derived from the vendor so consecutive invoices differ. */
function variantOf(inv: Invoice) {
  if (inv.template === 'classic' || inv.template === 'ledger' || inv.template === 'studio') return inv.template;
  const variants = ['classic', 'ledger', 'studio'] as const;
  return variants[[...inv.vendor.name].reduce((n, ch) => n + ch.charCodeAt(0), 0) % 3]!;
}

export function GenerateGoodsButton({ kind, runId }: { kind: 'payroll' | 'thr'; runId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post<{ invoices: number; periodId: string; unusedBudget: string }>(`/api/goods/${kind}/${runId}`),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['goods-periods'] });
      toast({ title: r.invoices ? `${r.invoices} goods-purchase invoice${r.invoices === 1 ? '' : 's'} ready` : 'No second-company payments in this run', body: Number(r.unusedBudget) ? `${idr(r.unusedBudget)} could not be matched to stock` : undefined });
    },
    onError: (e) => toast({ title: e instanceof ApiError ? e.message : 'Failed', tone: 'error' }),
  });
  return <Button icon="file" loading={m.isPending} onClick={() => m.mutate()}>Goods invoices</Button>;
}
